import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPin, Building2, Clock, Save, Phone, Camera } from 'lucide-react'
import { useUser } from '../contexts/UserContext'
import { upsertFoodBankProfile } from '../api/foodBanks'
import { uploadProfilePhoto } from '../api/profile'
import { requestPhoneOtp } from '../api/authVerification'

const FoodBankOnboarding = () => {
  const navigate = useNavigate()
  const { user, refreshUser } = useUser()

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    organizationName: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    hours: '',
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [testingPhone, setTestingPhone] = useState(false)
  const [phoneTestMessage, setPhoneTestMessage] = useState<string | null>(null)
  const [directoryPhoto, setDirectoryPhoto] = useState<File | null>(null)
  const [directoryPhotoPreview, setDirectoryPhotoPreview] = useState<string | null>(null)

  useEffect(() => {
    if (!user) return
    if (user.userType !== 'FOOD_BANK') {
      navigate('/')
      return
    }
    if (user.onboardingCompleted) {
      navigate('/dashboard')
      return
    }
    // Check email verification - must verify email before onboarding
    if (!user.emailVerified) {
      navigate('/verify-email')
      return
    }
  }, [user, navigate])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (error) setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.firstName || !formData.lastName || !formData.organizationName || !formData.phone || !formData.street || !formData.city || !formData.state || !formData.zipCode) {
      setError('Please fill in all required fields.')
      return
    }

    setLoading(true)
    try {
      // Update user profile with name and organization info
      await upsertFoodBankProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        name: formData.organizationName,
        phone: formData.phone.trim(),
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        hours: formData.hours ? formData.hours : undefined,
        latitude: formData.latitude,
        longitude: formData.longitude
      })

      if (directoryPhoto) {
        await uploadProfilePhoto(directoryPhoto)
      }

      await refreshUser()
      navigate('/dashboard', { replace: true })
    } catch (err: any) {
      setError(err.message || 'Failed to save food bank profile.')
    } finally {
      setLoading(false)
    }
  }

  const handleTestPhone = async () => {
    setPhoneTestMessage(null)
    setError(null)
    if (!formData.phone.trim()) {
      setError('Enter a phone number before sending a test text.')
      return
    }
    setTestingPhone(true)
    try {
      const res = await requestPhoneOtp({ phone: formData.phone.trim(), testOnly: true })
      setPhoneTestMessage(
        res.devCode
          ? `Test text sent (dev code: ${res.devCode}). Phone verification is optional for food banks.`
          : 'Test text sent. Check your phone — verification is optional for food banks.'
      )
    } catch (err: any) {
      setError(err.message || 'Failed to send test text.')
    } finally {
      setTestingPhone(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <Building2 className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Food Bank Setup</h2>
          <p className="mt-2 text-sm text-gray-600">
            Add the essentials so suppliers and volunteers can find you.
          </p>
        </div>

        <form className="bg-white rounded-lg shadow p-6 space-y-4" onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Contact First Name <span className="text-red-500">*</span>
              </label>
              <input
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="John"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Contact Last Name <span className="text-red-500">*</span>
              </label>
              <input
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Doe"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Organization Name <span className="text-red-500">*</span>
            </label>
            <input
              name="organizationName"
              value={formData.organizationName}
              onChange={handleChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., Downtown Community Food Bank"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 relative">
              <input
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                className="block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Business contact phone"
                required
              />
              <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Phone verification is optional. You only need email and business info for approval.
            </p>
            <button
              type="button"
              onClick={handleTestPhone}
              disabled={testingPhone || !formData.phone.trim()}
              className="mt-2 btn-outline text-sm disabled:opacity-50"
            >
              {testingPhone ? 'Sending…' : 'Send test text'}
            </button>
            {phoneTestMessage && (
              <p className="mt-2 text-xs text-green-700">{phoneTestMessage}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Address <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 grid grid-cols-1 gap-3">
              <div className="relative">
                <input
                  name="street"
                  value={formData.street}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Street address"
                  required
                />
                <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="City"
                  required
                />
                <input
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="State"
                  required
                />
                <input
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="ZIP"
                  required
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Hours (optional)</label>
            <div className="mt-1 relative">
              <input
                name="hours"
                value={formData.hours}
                onChange={handleChange}
                className="block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="e.g., Mon–Fri 9am–5pm"
              />
              <Clock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Directory photo <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <p className="mt-1 text-xs text-gray-500">
              Shown on the public Food Banks directory after your account is approved.
            </p>
            <div className="mt-2 flex items-center gap-3">
              {directoryPhotoPreview ? (
                <img
                  src={directoryPhotoPreview}
                  alt="Directory preview"
                  className="h-20 w-28 rounded-md object-cover border border-gray-200"
                />
              ) : (
                <div className="h-20 w-28 rounded-md border border-dashed border-gray-300 flex items-center justify-center">
                  <Camera className="h-5 w-5 text-gray-400" />
                </div>
              )}
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null
                  setDirectoryPhoto(file)
                  setDirectoryPhotoPreview(file ? URL.createObjectURL(file) : null)
                }}
                className="block w-full text-sm text-gray-600"
              />
            </div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-gray-500">
              {typeof formData.latitude === 'number' && typeof formData.longitude === 'number'
                ? `Location saved for distance estimates.`
                : `Optional: save your location to enable distance estimates.`}
            </div>
            <button
              type="button"
              onClick={() => {
                if (!navigator.geolocation) {
                  setError('Geolocation is not supported by your browser.')
                  return
                }
                navigator.geolocation.getCurrentPosition(
                  (pos) => {
                    setFormData((prev) => ({
                      ...prev,
                      latitude: pos.coords.latitude,
                      longitude: pos.coords.longitude,
                    }))
                    setError(null)
                  },
                  () => setError('Unable to get your location. Please allow location access and try again.'),
                  { enableHighAccuracy: false, timeout: 10000 }
                )
              }}
              className="btn-outline"
            >
              Use my location
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Saving...' : 'Save & Continue'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default FoodBankOnboarding


